import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Inline,
  InlineItem,
  Input,
  Placeholder,
  Stack,
  useKeyboardShortcut,
} from '@kinesis/bungle';
import { isEmpty } from 'lodash/fp';
import Fuse from 'fuse.js';
import { Disclosure } from '@/types';
import { disclosurePath } from '@/links';
import { shortcuts } from '@/shortcuts';
import { Item } from './item';
import { LegacyGpcItem } from './legacy-gpc-item';

const fuseOptions = {
  isCaseSensitive: false,
  shouldSort: true,
  includeMatches: false,
  findAllMatches: true,
  minMatchCharLength: 0,
  keys: ['name', 'id'],
};

// Pure list of disclosures for a community, with name search — mirrors
// `scenarios-list`. The page owns the RTK call and passes the data down;
// this component is store-less + Storybook-renderable per the front-end
// component/page split. Fuzzy search over name/id; Enter opens the top
// match; Escape clears. No delete this phase (Phase 5 polish).
const DisclosuresList = (props: DisclosuresListProps) => {
  const { disclosures, inventoryId, now, legacyGpcExists = false } = props;
  const navigate = useNavigate();
  const ref = useKeyboardShortcut({
    predicate: shortcuts.SEARCH_ITEMS.handler,
    callback: (event) => {
      event.preventDefault();
      ref.current?.focus();
    },
  });
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (isEmpty(search)) return disclosures;
    const fuse = new Fuse(disclosures, fuseOptions);
    return fuse.search(search).map(({ item }) => item);
  }, [search, disclosures]);
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setSearch('');
        ref.current?.blur();
      } else if (event.key === 'Enter' && filtered.length > 0) {
        navigate(disclosurePath(inventoryId, filtered[0].id));
      }
    },
    [ref, navigate, filtered, inventoryId],
  );

  return (
    <Stack space='medium'>
      <Inline>
        <InlineItem sizing='fill-container'>
          <Input
            ref={ref as any}
            type='search'
            placeholder={`Search (${shortcuts.SEARCH_ITEMS.mnemonic})`}
            onChange={setSearch}
            onKeyDown={onKeyDown as any}
            value={search}
            magnitude='large'
            autoFocus
          />
        </InlineItem>
      </Inline>
      {/* The legacy GPC report is a synthetic, pinned entry — not a searchable
          `Disclosure` — so it shows only in the unfiltered list, at the top. */}
      {legacyGpcExists && isEmpty(search) && (
        <LegacyGpcItem inventoryId={inventoryId} />
      )}
      {filtered.map((disclosure) => (
        <Item
          key={disclosure.id}
          disclosure={disclosure}
          inventoryId={inventoryId}
          now={now}
        />
      ))}
      {isEmpty(search) && isEmpty(filtered) && !legacyGpcExists && (
        <Placeholder>There are no disclosures.</Placeholder>
      )}
      {!isEmpty(search) && isEmpty(filtered) && (
        <Placeholder>There are no matching disclosures.</Placeholder>
      )}
    </Stack>
  );
};

export type DisclosuresListProps = {
  disclosures: Disclosure[];
  inventoryId: number;
  now: string;
  // The inventory has a stored legacy GPC report — render the synthetic,
  // non-deletable entry linking to `pages/gpc`.
  legacyGpcExists?: boolean;
};

export { DisclosuresList };
